/**
 * Target Cache Service
 *
 * Caching for target search results and targeting categories
 * Uses centralized IndexedDB storage utilities for consistency and performance
 */

import {
  getStorageItem,
  setStorageItem,
  removeStorageItem,
  getAllItems,
  clearStore,
  cleanupExpiredEntries as cleanupExpiredEntriesInStore,
  getStoreStats,
  isIndexedDBAvailable as checkIndexedDBAvailable,
  STORAGE_CONFIG,
  type StorageItem,
  getStore,
} from "@/utils/storage/indexed-db-storage";
import type {
  TargetSearchType,
  GenericTargetResult,
  CachedTargetData,
  TargetingCategoryClass,
  TargetingCategoryItem,
} from "./ctwa-ads.types";
import { TARGET_CACHE_CONFIG } from "./ctwa-ads.types";

const {
  CACHE_DURATION_MS,
  CATEGORY_CACHE_DURATION_MS,
  MAX_ENTRIES_PER_TYPE,
} = TARGET_CACHE_CONFIG;

// ============================================
// Types (Extended for cache entries)
// ============================================

interface CachedTargetEntry extends CachedTargetData {
  cacheKey: string;
}

interface CachedCategoryEntry {
  categoryClass: TargetingCategoryClass;
  data: TargetingCategoryItem[];
  timestamp: number;
  expiresAt: number;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Generate cache key from type and query
 */
const generateCacheKey = (type: TargetSearchType, query: string): string => {
  return `${type}:${query.toLowerCase().trim()}`;
};

// ============================================
// Cache Operations
// ============================================

/**
 * Get cached data for a specific type and query
 */
export const getCachedTargetData = async (
  type: TargetSearchType,
  query: string
): Promise<GenericTargetResult[] | null> => {
  try {
    const cacheKey = generateCacheKey(type, query);
    const result = await getStorageItem<CachedTargetEntry>("ctwaTargetSearch", cacheKey);

    if (!result) {
      return null;
    }

    return result.data;
  } catch (error) {
    console.error("[TargetCache] Error accessing cache:", error);
    return null;
  }
};

/**
 * Store target data in cache
 */
export const setCachedTargetData = async (
  type: TargetSearchType,
  query: string,
  data: GenericTargetResult[]
): Promise<void> => {
  try {
    const cacheKey = generateCacheKey(type, query);
    const now = Date.now();

    const cacheEntry: CachedTargetEntry = {
      cacheKey,
      type,
      query: query.toLowerCase().trim(),
      data,
      timestamp: now,
      expiresAt: now + CACHE_DURATION_MS,
    };

    await setStorageItem("ctwaTargetSearch", cacheKey, cacheEntry, CACHE_DURATION_MS);

    // Clean up old entries for this type after storing
    cleanupOldEntries(type).catch(console.error);
  } catch (error) {
    console.error("[TargetCache] Error storing cache:", error);
  }
};

/**
 * Delete specific cached data
 */
export const deleteCachedData = async (
  type: TargetSearchType,
  query: string
): Promise<void> => {
  try {
    const cacheKey = generateCacheKey(type, query);
    await removeStorageItem("ctwaTargetSearch", cacheKey);
  } catch (error) {
    console.error("[TargetCache] Error deleting cache:", error);
  }
};

/**
 * Get all cached data for a specific type
 */
export const getAllCachedDataByType = async (
  type: TargetSearchType
): Promise<GenericTargetResult[]> => {
  try {
    const allItems = await getAllItems<CachedTargetEntry>("ctwaTargetSearch");
    const validData: GenericTargetResult[] = [];

    Object.values(allItems).forEach((entry) => {
      if (entry.type === type) {
        validData.push(...entry.data);
      }
    });

    // Deduplicate by id
    const uniqueData = Array.from(
      new Map(validData.map((item) => [item.id, item])).values()
    );

    return uniqueData;
  } catch (error) {
    console.error("[TargetCache] Error accessing cache:", error);
    return [];
  }
};

/**
 * Clean up old entries for a specific type (keep only MAX_ENTRIES_PER_TYPE)
 */
const cleanupOldEntries = async (type: TargetSearchType): Promise<void> => {
  try {
    const allItems = await getAllItems<CachedTargetEntry>("ctwaTargetSearch");
    const entries: CachedTargetEntry[] = [];

    Object.values(allItems).forEach((entry) => {
      if (entry.type === type) {
        entries.push(entry);
      }
    });

    if (entries.length <= MAX_ENTRIES_PER_TYPE) {
      return;
    }

    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a.timestamp - b.timestamp);

    // Delete oldest entries
    const entriesToDelete = entries.slice(0, entries.length - MAX_ENTRIES_PER_TYPE);
    await Promise.all(
      entriesToDelete.map((entry) => removeStorageItem("ctwaTargetSearch", entry.cacheKey))
    );
  } catch (error) {
    console.error("[TargetCache] Error cleaning up old entries:", error);
  }
};

/**
 * Clean up all expired entries
 */
export const cleanupExpiredEntries = async (): Promise<number> => {
  return cleanupExpiredEntriesInStore("ctwaTargetSearch");
};

/**
 * Clear all cached data
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    await clearStore("ctwaTargetSearch");
    console.log("[TargetCache] All cache cleared");
  } catch (error) {
    console.error("[TargetCache] Error clearing cache:", error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async (): Promise<{
  totalEntries: number;
  entriesByType: Record<string, number>;
  totalSizeEstimate: number;
}> => {
  try {
    const stats = await getStoreStats("ctwaTargetSearch");

    // Convert entriesByKey to entriesByType for backward compatibility
    return {
      totalEntries: stats.totalEntries,
      entriesByType: stats.entriesByKey,
      totalSizeEstimate: stats.totalSizeEstimate,
    };
  } catch (error) {
    console.error("[TargetCache] Error getting cache stats:", error);
    return { totalEntries: 0, entriesByType: {}, totalSizeEstimate: 0 };
  }
};

// ============================================
// Category Cache Operations
// ============================================

/**
 * Get cached category data
 */
export const getCachedCategoryData = async (
  categoryClass: TargetingCategoryClass
): Promise<TargetingCategoryItem[] | null> => {
  try {
    const result = await getStorageItem<CachedCategoryEntry>(
      "ctwaTargetingCategories",
      categoryClass
    );

    if (!result) {
      return null;
    }

    return result.data;
  } catch (error) {
    console.error("[TargetCache] Error accessing category cache:", error);
    return null;
  }
};

/**
 * Store category data in cache
 */
export const setCachedCategoryData = async (
  categoryClass: TargetingCategoryClass,
  data: TargetingCategoryItem[]
): Promise<void> => {
  try {
    const now = Date.now();

    const cacheEntry: CachedCategoryEntry = {
      categoryClass,
      data,
      timestamp: now,
      expiresAt: now + CATEGORY_CACHE_DURATION_MS,
    };

    await setStorageItem(
      "ctwaTargetingCategories",
      categoryClass,
      cacheEntry,
      STORAGE_CONFIG.TARGETING_CATEGORY_TTL_MS
    );
    console.log(
      `[TargetCache] Cached ${data.length} items for category: ${categoryClass}`
    );
  } catch (error) {
    console.error("[TargetCache] Error storing category cache:", error);
  }
};

/**
 * Delete specific cached category data
 */
export const deleteCachedCategoryData = async (
  categoryClass: TargetingCategoryClass
): Promise<void> => {
  try {
    await removeStorageItem("ctwaTargetingCategories", categoryClass);
  } catch (error) {
    console.error("[TargetCache] Error deleting category cache:", error);
  }
};

/**
 * Get all cached category data
 */
export const getAllCachedCategories = async (): Promise<
  Record<TargetingCategoryClass, TargetingCategoryItem[]>
> => {
  try {
    const allItems = await getAllItems<CachedCategoryEntry>("ctwaTargetingCategories");
    const categoriesMap = {} as Record<TargetingCategoryClass, TargetingCategoryItem[]>;

    Object.entries(allItems).forEach(([key, entry]) => {
      categoriesMap[key as TargetingCategoryClass] = entry.data;
    });

    return categoriesMap;
  } catch (error) {
    console.error("[TargetCache] Error accessing category cache:", error);
    return {} as Record<TargetingCategoryClass, TargetingCategoryItem[]>;
  }
};

/**
 * Check which categories are cached and valid
 */
export const getCachedCategoryStatus = async (): Promise<
  Record<
    TargetingCategoryClass,
    { cached: boolean; expiresAt?: number; itemCount?: number }
  >
> => {
  try {
    const store = getStore("ctwaTargetingCategories");
    const now = Date.now();
    const statusMap = {} as Record<
      TargetingCategoryClass,
      { cached: boolean; expiresAt?: number; itemCount?: number }
    >;

    await store.iterate<StorageItem<CachedCategoryEntry>, void>((item, key) => {
      const entry = item.data;
      const isValid = !item.expiresAt || item.expiresAt > now;
      statusMap[key as TargetingCategoryClass] = {
        cached: isValid,
        expiresAt: isValid ? item.expiresAt : undefined,
        itemCount: isValid ? entry.data.length : undefined,
      };
    });

    return statusMap;
  } catch (error) {
    console.error("[TargetCache] Error getting category cache status:", error);
    return {} as Record<
      TargetingCategoryClass,
      { cached: boolean; expiresAt?: number; itemCount?: number }
    >;
  }
};

/**
 * Clear all category cache
 */
export const clearAllCategoryCache = async (): Promise<void> => {
  try {
    await clearStore("ctwaTargetingCategories");
    console.log("[TargetCache] All category cache cleared");
  } catch (error) {
    console.error("[TargetCache] Error clearing category cache:", error);
  }
};

/**
 * Check if IndexedDB is available
 */
export const isIndexedDBAvailable = (): boolean => {
  return checkIndexedDBAvailable();
};

/**
 * Close database connection and clear stores
 * For logout cleanup
 */
export const closeDatabase = (): void => {
  try {
    Promise.all([
      clearStore("ctwaTargetSearch"),
      clearStore("ctwaTargetingCategories"),
    ]).catch(console.error);
    console.log("[TargetCache] Database stores cleared");
  } catch (error) {
    console.error("[TargetCache] Error closing database:", error);
  }
};

/**
 * Drop the entire database (for complete cleanup)
 */
export const dropDatabase = async (): Promise<void> => {
  try {
    await Promise.all([
      clearStore("ctwaTargetSearch"),
      clearStore("ctwaTargetingCategories"),
    ]);
    console.log("[TargetCache] Database dropped");
  } catch (error) {
    console.error("[TargetCache] Error dropping database:", error);
  }
};

// Export for testing
export const __test__ = {
  generateCacheKey,
};
